import { useState } from 'react';
import { Check, Link2, MapPin, Plus, X } from 'lucide-react';
import { api } from '../../../../services/api';
import { useWorkspace } from '../../WorkspaceContext';
import WeeklyHoursEditor from '../../components/WeeklyHoursEditor';

const emptyLocation = { name: '', address: '', city: '', country: '', phone: '' };

// Business-level view: every location, each with its own address, opening
// hours and booking link. Staff, services, clients and the calendar are all
// scoped to whichever location is selected in the header switcher.
const ManageLocations = () => {
  const { locations, activeLocation, setActiveLocationId, refresh } = useWorkspace();
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyLocation);
  const [editingId, setEditingId] = useState(null);
  const [hoursDraft, setHoursDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [defaultingId, setDefaultingId] = useState(null);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyLocation);
    setHoursDraft(null);
    setFormOpen(true);
  };

  const openEdit = (location) => {
    setEditingId(location.id);
    setForm({
      name: location.name || '',
      address: location.address || '',
      city: location.city || '',
      country: location.country || '',
      phone: location.phone || '',
    });
    setHoursDraft(location.workingHours || null);
    setFormOpen(true);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    const payload = {
      name: form.name.trim(),
      address: form.address.trim(),
      city: form.city.trim(),
      country: form.country.trim(),
      phone: form.phone.trim() || null,
    };

    if (editingId && hoursDraft) {
      payload.workingHours = hoursDraft;
    }

    const result = editingId
      ? await api.updateWorkspaceLocation(editingId, payload)
      : await api.createWorkspaceLocation(payload);

    setSaving(false);
    if (result.error) {
      setError(result.error);
      return;
    }

    setFormOpen(false);
    // Jump straight into the new location so its setup (staff, services, link)
    // is what the rest of the workspace shows.
    if (!editingId && result.data?.id) {
      setActiveLocationId(result.data.id);
    }
    await refresh();
  };

  const removeLocation = async (location) => {
    setError('');
    const result = await api.deleteWorkspaceLocation(location.id);
    if (result.error) {
      setError(result.error);
      return;
    }
    if (activeLocation?.id === location.id) {
      setActiveLocationId(null);
    }
    await refresh();
  };

  const makeDefault = async (location) => {
    if (location.isDefault) return;
    setError('');
    setDefaultingId(location.id);
    const result = await api.setDefaultWorkspaceLocation(location.id);
    setDefaultingId(null);
    if (result.error) {
      setError(result.error);
      return;
    }
    await refresh();
  };

  return (
    <div className="min-w-0">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h2 className="font-display text-2xl font-light text-ink-900 sm:text-3xl">Locations</h2>
          <p className="mt-1 max-w-2xl text-sm text-ink-700/80">
            Each location runs its own calendar, clients, staff, services and booking link. The
            default location is used for the main booking fallback and business address.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex flex-none items-center justify-center gap-2 bg-saffron-500 px-4 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400"
        >
          <Plus className="h-4 w-4" />
          Add location
        </button>
      </div>

      {error && (
        <div className="mt-4 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="mt-6 space-y-4">
        {locations.map((location) => {
          const isActive = activeLocation?.id === location.id;
          return (
            <div key={location.id} className="bg-sand-50 p-5 ring-1 ring-ink-900/10 sm:p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex min-w-0 items-start gap-4">
                  <span className="flex h-11 w-11 flex-none items-center justify-center bg-ink-900">
                    <MapPin className="h-5 w-5 text-saffron-400" />
                  </span>
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 font-medium text-ink-900">
                      {location.name}
                      {location.isDefault && (
                        <span className="bg-ink-900/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-600/70">
                          Default
                        </span>
                      )}
                      {isActive && (
                        <span className="bg-saffron-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-saffron-700">
                          Selected
                        </span>
                      )}
                    </p>
                    <p className="mt-1 text-sm text-ink-700/75">
                      {[location.address, location.city, location.country]
                        .filter(Boolean)
                        .join(', ') || 'No address yet'}
                    </p>
                    <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-ink-700/75">
                      <Link2 className="h-3.5 w-3.5 text-saffron-600" />
                      /book/{location.slug}
                      {location.bookingEnabled === false && (
                        <span className="ml-1 text-xs text-red-700">(booking off)</span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex flex-none flex-wrap gap-2">
                  {!isActive && (
                    <button
                      type="button"
                      onClick={() => setActiveLocationId(location.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-ink-900 ring-1 ring-ink-900/20 hover:bg-white"
                    >
                      <Check className="h-3.5 w-3.5" />
                      Work here
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => openEdit(location)}
                    className="px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-ink-900 ring-1 ring-ink-900/20 hover:bg-white"
                  >
                    Edit
                  </button>
                  {!location.isDefault && (
                    <button
                      type="button"
                      onClick={() => makeDefault(location)}
                      disabled={defaultingId === location.id}
                      className="px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-ink-900 ring-1 ring-ink-900/20 hover:bg-white disabled:opacity-50"
                    >
                      {defaultingId === location.id ? 'Updating…' : 'Make default'}
                    </button>
                  )}
                  {!location.isDefault && (
                    <button
                      type="button"
                      onClick={() => removeLocation(location)}
                      className="px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-red-700 ring-1 ring-red-200 hover:bg-red-50"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-ink-950/50 p-4 sm:items-center">
          <div className="my-8 w-full max-w-xl bg-sand-50 p-6 shadow-[0_30px_60px_-30px_rgba(6,18,15,0.55)] ring-1 ring-ink-900/10 sm:p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl font-light text-ink-900">
                {editingId ? 'Edit location' : 'Add location'}
              </h2>
              <button type="button" onClick={() => setFormOpen(false)} aria-label="Close">
                <X className="h-5 w-5 text-ink-700" />
              </button>
            </div>

            <form className="mt-6 space-y-4" onSubmit={handleSave}>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Location name
                </label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                  placeholder="Lekki studio"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Street address <span className="text-saffron-600">*</span>
                </label>
                <input
                  required
                  value={form.address}
                  onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))}
                  className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                  placeholder="14 Admiralty Way"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                    City <span className="text-saffron-600">*</span>
                  </label>
                  <input
                    required
                    value={form.city}
                    onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value }))}
                    className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                    placeholder="Lagos"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                    Country <span className="text-saffron-600">*</span>
                  </label>
                  <input
                    required
                    value={form.country}
                    onChange={(e) => setForm((prev) => ({ ...prev, country: e.target.value }))}
                    className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                    placeholder="Nigeria"
                  />
                </div>
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Phone
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                  className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                />
              </div>

              {editingId && (
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                    Opening hours
                  </label>
                  <WeeklyHoursEditor value={hoursDraft} onChange={setHoursDraft} />
                </div>
              )}

              <button
                type="submit"
                disabled={saving}
                className="w-full bg-saffron-500 px-5 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 disabled:opacity-50"
              >
                {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add location'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageLocations;
